import React, { useEffect, useState } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Download, AlertTriangle, CheckCircle2, Stethoscope, Home, Pill, CalendarHeart, Leaf, Loader2, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { api } from '../lib/api';
import { openPdf } from '../lib/downloads';
import { t } from '../i18n';
import { isUserLoggedIn } from '../lib/auth';

const SEV = {
  low:       { color: '#2A5244', label: 'Low',       bg: '#E8F5E9', icon: CheckCircle2 },
  moderate:  { color: '#E07A5F', label: 'Moderate',  bg: '#FDECE4', icon: AlertTriangle },
  high:      { color: '#D9534F', label: 'High',      bg: '#FEE2E2', icon: AlertTriangle },
  emergency: { color: '#B00020', label: 'Emergency', bg: '#FEE2E2', icon: AlertTriangle },
};

export default function Report({ lang, publicView = false }) {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const shareToken = searchParams.get('token') || '';
  const [r, setR] = useState(null);
  const [err, setErr] = useState('');
  // useEffect(() => {
  //   api.get(`/report/${id}`).then((res) => setR(res.data)).catch(() => {});
  // }, [id]);
  useEffect(() => {
    if (publicView) return;
    if (!isUserLoggedIn()) return setErr('Please login to view your report.');
  }, [publicView]);

  useEffect(() => {
  if (!publicView && !isUserLoggedIn()) return;
  if (publicView && !shareToken) {
    setErr('This report link is missing its secure token.');
    return;
  }
  const fetchReport = async () => {
    try {
      const path = publicView
        ? `/public/report/${id}?token=${encodeURIComponent(shareToken)}`
        : `/report/${id}`;
      const res = await api.get(path);
      setR(res.data);
      setErr('');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Could not load report. Please try again.';
      setErr(msg);
    }
  };

  fetchReport();
}, [id, publicView, shareToken]);
  if (err) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center">
      <div className="text-[#D9534F] font-semibold text-lg">{err}</div>
      <p className="text-[#7A7A7A] mt-2">Please check the report link or generate the report again.</p>
      <Link to="/chat" className="inline-flex mt-5 text-[#1E3F33] font-semibold">Go back to chat</Link>
    </div>
  );
  if (!r) return (
    <div className="max-w-3xl mx-auto px-4 py-20 text-center text-[#7A7A7A]">
      <Loader2 className="h-8 w-8 animate-spin mx-auto mb-3"/>Loading report…
    </div>
  );
  const sev = SEV[r.severity] || SEV.low;
  const SevIcon = sev.icon;
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 sm:py-12 fade-up">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <Link to={publicView ? '/' : '/chat'} className="flex items-center gap-2 text-[#1E3F33] font-semibold" data-testid="report-back-link">
          <ArrowLeft className="h-4 w-4"/> {t(lang,'back')}
        </Link>
        <div className="flex flex-col sm:flex-row flex-wrap gap-2">
          {!publicView && (
            <Link to={`/doctors?report=${id}`}>
              <Button className="w-full sm:w-auto rounded-full bg-[#1E3F33] hover:bg-[#2A5244] text-white h-12 px-6 font-semibold" data-testid="send-to-doctor-btn">
                <Stethoscope className="h-4 w-4 mr-2"/> Send to a doctor
              </Button>
            </Link>
          )}
          <Button onClick={() => openPdf(publicView ? `/public/report/${id}/pdf?token=${encodeURIComponent(shareToken)}` : `/report/${id}/pdf`, `health-buddy-${id}.pdf`)} className="w-full sm:w-auto rounded-full bg-[#E07A5F] hover:bg-[#C9674D] text-white h-12 px-6 font-semibold" data-testid="download-pdf-btn">
            <Download className="h-4 w-4 mr-2"/> {t(lang,'download_pdf')}
          </Button>
        </div>
      </div>

      <div className="relative rounded-2xl sm:rounded-3xl overflow-hidden bg-[#1E3F33] text-white p-5 sm:p-10">
        <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full bg-[#E07A5F]/25 blur-3xl"/>
        <Badge className="rounded-full bg-white/15 hover:bg-white/15 border-white/20 text-white font-semibold px-3 py-1"><FileText className="h-3.5 w-3.5 mr-1.5"/>Clinical Intake Summary</Badge>
        <h1 className="font-display text-3xl sm:text-5xl mt-3">{t(lang,'report_ready')}</h1>
        <p className="text-white/80 mt-3 text-base sm:text-lg max-w-2xl break-words">{r.summary}</p>
        <div className="mt-6 flex flex-wrap gap-4 items-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: sev.bg, color: sev.color }}>
            <SevIcon className="h-5 w-5"/>
            <span className="font-semibold">{t(lang,'severity')}: {sev.label}</span>
          </div>
          <div className="text-white/70 text-sm">Report ID: {(r.id || '').slice(0,8)}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-6">
        {r.doctor_handoff ? (
          <Card className="p-6 sm:p-7 rounded-3xl bg-white border border-[#E8E1D5] hb-shadow-soft md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <Stethoscope className="h-6 w-6 text-[#1E3F33]" />
              <h3 className="font-display text-2xl">Doctor Handoff</h3>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 text-[15px]">
              <div className="rounded-2xl bg-[#F4F1EA] p-4">
                <div className="text-xs uppercase tracking-wide text-[#7A7A7A] font-semibold">Chief complaint</div>
                <div className="mt-1 text-[#1A1A1A] font-semibold break-words">{r.doctor_handoff.chief_complaint || 'Not clearly reported'}</div>
              </div>
              <div className="rounded-2xl bg-[#F4F1EA] p-4">
                <div className="text-xs uppercase tracking-wide text-[#7A7A7A] font-semibold">Onset / severity</div>
                <div className="mt-1 text-[#1A1A1A] break-words">{r.doctor_handoff.onset_duration || 'Not reported'} · {r.doctor_handoff.severity || 'Not reported'}</div>
              </div>
              <div className="rounded-2xl bg-[#F4F1EA] p-4">
                <div className="text-xs uppercase tracking-wide text-[#7A7A7A] font-semibold">Reported positives</div>
                <div className="mt-1 text-[#1A1A1A] break-words">{(r.doctor_handoff.reported_positives || []).join(', ') || 'Not clearly reported'}</div>
              </div>
              <div className="rounded-2xl bg-[#F4F1EA] p-4">
                <div className="text-xs uppercase tracking-wide text-[#7A7A7A] font-semibold">Reported negatives</div>
                <div className="mt-1 text-[#1A1A1A] break-words">{(r.doctor_handoff.reported_negatives || []).join(', ') || 'Not clearly reported'}</div>
              </div>
              <div className="rounded-2xl bg-[#F4F1EA] p-4">
                <div className="text-xs uppercase tracking-wide text-[#7A7A7A] font-semibold">Vitals</div>
                <div className="mt-1 text-[#1A1A1A] break-words">
                  {Object.entries(r.doctor_handoff.vitals || {}).map(([k, v]) => `${k.replaceAll('_', ' ')}: ${v}`).join(', ') || 'Not reported'}
                </div>
              </div>
              <div className="rounded-2xl bg-[#F4F1EA] p-4">
                <div className="text-xs uppercase tracking-wide text-[#7A7A7A] font-semibold">Medicines / allergies</div>
                <div className="mt-1 text-[#1A1A1A] break-words">{r.doctor_handoff.medications_allergies || 'Not reported'}</div>
              </div>
            </div>
          </Card>
        ) : null}

        <Card className="p-6 sm:p-7 rounded-3xl bg-white border border-[#E8E1D5] hb-shadow-soft">
          <div className="flex items-center gap-3 mb-3"><Stethoscope className="h-6 w-6 text-[#1E3F33]"/><h3 className="font-display text-2xl">{t(lang,'conditions')}</h3></div>
          <ul className="space-y-3">
            {r.likely_conditions?.map((c, i) => (
              <li key={i} className="p-4 rounded-2xl bg-[#F4F1EA]">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-semibold text-[#1A1A1A]">{c.name}</div>
                  <Badge variant="outline" className="rounded-full border-[#1E3F33] text-[#1E3F33] text-xs">{c.probability}</Badge>
                </div>
                <div className="text-[15px] text-[#4A4A4A] mt-1">{c.explanation}</div>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="p-6 sm:p-7 rounded-3xl bg-white border border-[#E8E1D5] hb-shadow-soft">
          <div className="flex items-center gap-3 mb-3"><AlertTriangle className="h-6 w-6 text-[#D9534F]"/><h3 className="font-display text-2xl">{t(lang,'red_flags')}</h3></div>
          {r.red_flags?.length ? (
            <ul className="space-y-2 text-[16px] text-[#1A1A1A] list-disc pl-5">
              {r.red_flags.map((x,i)=><li key={i}>{x}</li>)}
            </ul>
          ) : (
            <div className="text-[#7A7A7A]">None reported. Stay alert and monitor your symptoms.</div>
          )}
        </Card>

        <Card className="p-6 sm:p-7 rounded-3xl bg-white border border-[#E8E1D5] hb-shadow-soft">
          <div className="flex items-center gap-3 mb-3"><Home className="h-6 w-6 text-[#1E3F33]"/><h3 className="font-display text-2xl">{t(lang,'home_care')}</h3></div>
          <ul className="space-y-2 text-[16px] text-[#1A1A1A] list-disc pl-5">
            {r.home_care?.map((x,i)=><li key={i}>{x}</li>)}
          </ul>
        </Card>

        <Card className="p-6 sm:p-7 rounded-3xl bg-white border border-[#E8E1D5] hb-shadow-soft">
          <div className="flex items-center gap-3 mb-3"><Pill className="h-6 w-6 text-[#1E3F33]"/><h3 className="font-display text-2xl">{t(lang,'otc')}</h3></div>
          <ul className="space-y-2 text-[16px] text-[#1A1A1A] list-disc pl-5">
            {r.medications_otc?.map((x,i)=><li key={i}>{x}</li>)}
          </ul>
        </Card>

        <Card className="p-6 sm:p-7 rounded-3xl bg-white border border-[#E8E1D5] hb-shadow-soft md:col-span-2">
          <div className="flex items-center gap-3 mb-3"><CalendarHeart className="h-6 w-6 text-[#E07A5F]"/><h3 className="font-display text-2xl">{t(lang,'when_doc')}</h3></div>
          <p className="text-[16px] leading-relaxed text-[#1A1A1A]">{r.when_to_see_doctor}</p>
        </Card>

        <Card className="p-6 sm:p-7 rounded-3xl bg-white border border-[#E8E1D5] hb-shadow-soft md:col-span-2">
          <div className="flex items-center gap-3 mb-3"><Leaf className="h-6 w-6 text-[#1E3F33]"/><h3 className="font-display text-2xl">{t(lang,'lifestyle')}</h3></div>
          <ul className="space-y-2 text-[16px] text-[#1A1A1A] list-disc pl-5 sm:grid sm:grid-cols-2 sm:gap-x-6">
            {r.lifestyle_advice?.map((x,i)=><li key={i}>{x}</li>)}
          </ul>
        </Card>
      </div>

      <p className="text-xs text-[#7A7A7A] mt-6 italic">{r.disclaimer}</p>
    </div>
  );
}
